// MAVULA Agent Marketing Hub — centralized immutable templates.
// Do NOT hard-code these strings elsewhere. Update in one place only.
// Version tag is surfaced in the UI so agents know they have the latest set.
//
// COMPLIANCE NOTE (updated for UK s.21 FSMA + FCA MCOB):
// - No specific rate, yield, deposit % or return figures may appear in agent copy.
// - No "guarantee" language on legal title, returns or timings.
// - All terms are set by each project developer inside the signed sale agreement.
// - MAVULA Investment Ltd is not FCA-authorised and is not a mortgage lender.

export const MARKETING_VERSION = "Updated · Compliance lockdown (s.21 FSMA / MCOB)";

// The mandatory disclaimer that MUST appear on every shared piece.
// The UI checks copy content contains this before allowing share.
export const REQUIRED_DISCLAIMER =
  "Not a bank, remittance service or FCA-authorised mortgage lender. Terms disclosed in your individual project contract.";

// Immutable core lines — these are considered official brand copy and
// cannot be edited by agents. They render read-only in the UI.
export const MUST_INCLUDE = [
  "Title deed registered in your name on completion",
  "Modest deposit — no credit checks",
  "We never ask about immigration status abroad",
  "Same fair terms for locals & diaspora",
  "Not a bank, remittance service or FCA-authorised mortgage lender",
];

export const NEVER_SAY = [
  "Guaranteed returns / guaranteed title / guaranteed anything",
  "Fixed X% for Y years (never quote specific rates or deposits in public marketing)",
  "Fast money / quick flip",
  "We are a bank / we issue loans / we are FCA-authorised",
  "We can help with visas / immigration",
  "Better than [other country/company name] (keep it respectful)",
];

// --- Section 1: SOCIAL & WHATSAPP ---
export const SOCIAL_GENERAL = [
  "Own your land — your papers abroad don't matter. ✅ Title in your name on completion ✅ No status checks abroad → [Link]",
  "Dubai built a destination. We help build your family's home. Invest in your birthright → [Link]",
  "No credit checks, no hidden fees, no tricks. Fair land ownership for every citizen → [Link]",
  "Your land, your name, forever. We never ask about your visas abroad → [Link]",
  "Local citizens reserved first. Same fair price for everyone → [Link]",
  "Flexible instalment plans if life happens. We understand → [Link]",
  "Invest in what lasts — not hype. Real property, real communities → [Link]",
  "Pass it down simply. Keep your land in your family for generations → [Link]",
];

export const SHORT_QUOTES = [
  "No fine print, no tricks — just your land, your name, forever.",
  "The fairest way to come home — terms set in your contract, not in a headline.",
  "We don't care where you live now — only where you belong.",
  "Your visa abroad does not own your home back home.",
  "Build for your children, not just for today.",
  "Same price for your neighbour at home and you abroad.",
  "If life gets hard — flexible plans available. We stand with you.",
  "Independent local lawyers check every title, where local law permits.",
  // Impact-focused agent lines
  "With MAVULA, every home built creates jobs for people right here — your investment feeds families and builds skills.",
  "We're not just building houses — we're building work, training, and a future for thousands of Southern Africans.",
  "Your investment doesn't just sit there — it builds roads, pays wages, and teaches skills that last a lifetime.",
  "We don't import builders from far away — we train and employ people right where the land is.",
  "Every new home means more work for plumbers, more jobs for drivers, more income for local shops.",
  "You secure your legacy — and help thousands secure theirs too.",
  "Others build homes for people. We build people for their homes — and their communities.",
];

export const COUNTRY_TEMPLATES = [
  {
    flag: "🇿🇼",
    country: "Zimbabwe",
    headline: "Zimbabwean — Your Land Is Yours",
    bullets: [
      "Only need Zimbabwean ID / Passport — no foreign papers asked",
      "Title deed registered fully in your name — no shared ownership",
      "Projects in Harare, Bulawayo, Gweru — Borrowdale Sunrise available now",
      "Same terms for those at home or in UK, SA, Canada, anywhere",
    ],
    quote: "We know how hard it is when others ask for permits you don't have. We don't.",
    tags: "#Zimbabwe #OwnYourLand #Mavula",
  },
  {
    flag: "🇿🇲",
    country: "Zambia",
    headline: "Zambian — Secure Your Home Back Home",
    bullets: [
      "Only need Zambian citizenship proof — no immigration questions abroad",
      "Ibex Hill Residence Lusaka — available now",
      "Flexible payments aligned with farming or work income",
      "Independent lawyers registered in Zambia",
    ],
    quote: "Your status abroad does not take away your right to own land here.",
    tags: "#Zambia #Lusaka #HomeOwnership",
  },
  {
    flag: "🇧🇼",
    country: "Botswana",
    headline: "Botswana — Your Birthright, Your Terms",
    bullets: [
      "Only need Botswana ID — no extra documents",
      "Coming soon: Gaborone & Francistown estates",
      "Full freehold — no leasehold tricks",
      "Support for youth & women first-time buyers",
    ],
    quote: "Build your future in the land that raised you — no barriers.",
    tags: "#Botswana #Diaspora #Mavula",
  },
  {
    flag: "🇳🇦",
    country: "Namibia",
    headline: "Namibian — Roots You Can Keep Forever",
    bullets: [
      "Only need Namibian citizenship — no questions about your life abroad",
      "Coastal & inland land options coming soon",
      "Clear inheritance rules — easy to pass to family",
      "Aligned with Namibian land laws",
    ],
    quote: "Wherever you work now — your home is here.",
    tags: "#Namibia #LandRights #Mavula",
  },
  {
    flag: "🇿🇦",
    country: "South Africa",
    headline: "South Africa — Fair Access For All Citizens",
    bullets: [
      "Only need SA ID / Smart ID / Passport",
      "Melrose Arch Family Estate Johannesburg — pre-launch pricing now",
      "Local citizens reserved first",
      "Terms disclosed in your individual project contract",
    ],
    quote: "Own a piece of the future — on fair ground.",
    tags: "#SouthAfrica #Johannesburg #Property",
  },
];

// --- Section 2: PRINT & OUTREACH ---
export const FLYER_TEXT = `MAVULA INVESTMENT — AT A GLANCE

WHO WE ARE
Dignified, fair land ownership for Southern African citizens — at home or abroad.

WHAT YOU GET
✅ Full freehold title deed — registered in your name on completion
✅ Modest deposit only
✅ Flexible developer instalment plan — terms in your contract
✅ No immigration or visa questions
✅ Same terms for locals & diaspora
✅ Local citizens reserved first

HOW IT WORKS
1. Prove your citizenship
2. Choose your land/home
3. Local lawyers check your title
4. Pay flexibly — build your legacy

CONTACT
Agent: [Name] | [Phone/Email]
Website: [Link]

IMPORTANT
MAVULA Investment Ltd (UK Company No. 17368822) is not authorised or regulated by the FCA. Not a bank, remittance service or mortgage lender. All financial terms are set inside your individual signed project contract. Home-ownership plans are developer-set instalment arrangements. Capital at risk on investor products; property values can fall as well as rise.`;

export const EMAIL_TEMPLATE = `Subject: Own your land back home — fair terms for every citizen

Hello [First Name],

I hope this finds you well. I wanted to share something I believe genuinely helps our community — MAVULA Investment.

If you're a citizen (at home or abroad), you can look into owning a full freehold home with:
• Modest deposit — no credit checks
• Title deed registered in your name on completion
• Flexible developer instalment plans, disclosed in your signed contract
• No questions about your visa or immigration status
• Local citizens reserved first

If life gets hard, flexible options are available. Local lawyers review your title.

I'd love to walk you through 3 simple steps or show you the projects available now. Reply to this email or WhatsApp me any time.

Warmly,
[Your Name]
[Your Phone] · [Your Email]

Not a bank, remittance service or FCA-authorised mortgage lender. MAVULA Investment Ltd (UK Company No. 17368822) is not authorised or regulated by the FCA. Terms are set in your individual project contract.`;

export const SLIDES_OUTLINE = `MAVULA PRESENTATION — 6 SLIDES

Slide 1 — THE PROBLEM
"Our people work hard abroad — but back home, they're told they don't qualify."
• Foreign banks won't finance land at home
• Local schemes demand papers diaspora can't provide
• Prices keep rising while dignity is lost

Slide 2 — WHO WE ARE
MAVULA Investment: fair, dignified land ownership for Southern African citizens.
Local legal oversight in each country. Not a bank. Not a remittance service. Not FCA-authorised.

Slide 3 — HOW IT WORKS
1. Prove citizenship  2. Choose your project  3. Local lawyers check title  4. Pay flexibly

Slide 4 — WHAT MAKES US DIFFERENT
✅ Modest deposit · Flexible plan · No credit checks
✅ Title in your name on completion · Local citizens reserved first
✅ Same terms for locals & diaspora · Flexible plans if life gets hard

Slide 5 — PROJECTS
Zimbabwe · Zambia · South Africa · Botswana · Namibia (rolling out)

Slide 6 — CALL TO ACTION
Visit [Link] or WhatsApp your agent today.

Important: Not a bank, remittance service or FCA-authorised mortgage lender. Terms in your signed project contract.`;

// --- Section 3: BY AUDIENCE ---
export const AUDIENCE_TEMPLATES = [
  {
    audience: "Women & Mothers",
    headline: "Your name, your right — equal ownership for every woman",
    bullets: [
      "Title deed can be registered fully in your name",
      "No spousal consent tricks or extra hurdles",
      "Special guidance for single mothers",
      "Keep your land safe for your children",
    ],
    quote: "Your hard work deserves security that is yours alone.",
  },
  {
    audience: "Youth & Students",
    headline: "Start your future now — no need to wait until you're \"rich\"",
    bullets: [
      "Open to citizens aged 18+",
      "Modest deposit — start small, grow steady",
      "No credit history needed",
      "Learn property skills via Pula Learn",
    ],
    quote: "Your age doesn't stop you from owning your roots.",
  },
  {
    audience: "Returnees & Retirees",
    headline: "Come home to rest — secure your place",
    bullets: [
      "Simplified process for long-term diaspora returning home",
      "No rush — comfortable repayment horizons",
      "Clear inheritance for your family",
      "Independent legal help available locally",
    ],
    quote: "The land you left is waiting for you.",
  },
];

export const PHONE_SCRIPT = `PHONE / IN-PERSON SCRIPT — Simple, honest, ready to follow

"Thank you for asking. Many people get stuck because schemes ask for papers they don't have, or charge too much.

MAVULA is different: We only ask if you are a citizen of this country. We help you into full ownership with a modest deposit, no credit checks, and your title is registered in your name on completion.

We don't ask about your visas abroad. We don't push locals aside. And if you need flexibility with payments later — options are set out in your signed contract.

Would you like me to walk you through the 3 simple steps, or show you the projects available right now?

Important: MAVULA Investment Ltd is not a bank, remittance service or FCA-authorised mortgage lender. All financial terms live in your individual signed project contract."`;

// --- Section 4: HOW TO USE (rules) ---
export const HOW_TO_USE = [
  "Keep the core lines exactly as written — never change \"no status checks\", \"title in your name on completion\", \"modest deposit\".",
  "Add your name/phone at the end only.",
  "NEVER quote a specific rate, yield, deposit % or return figure in any public marketing — those are contract-only.",
  "NEVER use the word 'guarantee' for returns, title, or timings.",
  "Always include: \"Not a bank, remittance service or FCA-authorised mortgage lender. Terms in your individual project contract.\"",
  "For local languages: translate simply — keep the meaning clear.",
  "Share one caption at a time — keep it short and easy to read.",
];
